/**
 * The parked page's pictures as the server pushes them (server-sent events on
 * `/admin/live/:id/stream`), read with `fetch` so the session's token rides
 * along (an `<img>` or `EventSource` cannot send one).
 *
 * Resolves `'ended'` when the server says the wait is over, and rejects when
 * the stream could not be opened or dropped: the caller then falls back to
 * asking for a picture every second or so.
 */
import { useAuthStore } from '@/store/auth'

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api/v1'

export async function streamFrames(handoffId: string, onFrame: (jpeg: Blob) => void, signal: AbortSignal): Promise<'ended'> {
  const token = useAuthStore.getState().tokens?.accessToken
  const response = await fetch(`${BASE_URL}/admin/live/${encodeURIComponent(handoffId)}/stream`, {
    headers: { Accept: 'text/event-stream', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    signal,
  })
  if (!response.ok || !response.body) throw new Error(`stream ${response.status}`)

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  for (;;) {
    const { value, done } = await reader.read()
    if (done) throw new Error('stream dropped')
    buffer += decoder.decode(value, { stream: true })
    // Events are separated by a blank line; a picture can span several reads.
    let cut = buffer.indexOf('\n\n')
    while (cut !== -1) {
      const block = buffer.slice(0, cut)
      buffer = buffer.slice(cut + 2)
      cut = buffer.indexOf('\n\n')
      if (block.startsWith('event: end')) return 'ended'
      if (!block.startsWith('event: frame')) continue
      const data = block.slice(block.indexOf('data: ') + 6)
      try {
        const bytes = Uint8Array.from(atob(data), (char) => char.charCodeAt(0))
        onFrame(new Blob([bytes], { type: 'image/jpeg' }))
      } catch {
        /* a torn picture: the next one replaces it */
      }
    }
  }
}
